import * as Yup from 'yup';

import { SIGNATURE_CONSTRAINTS, SIGNATURE_DEFAULTS } from 'config/signatureFonts';
import type {
    CreateSignatureFormInterface,
    DrawnSignatureFormInterface,
    SignatureCreationMethod,
    TypedSignatureFormInterface,
    UploadSignatureFormInterface,
} from 'types';

const optionalMaxString = (max: number) =>
    Yup.string().trim().max(max, `Must be less than ${max} characters`).nullable();

const metadataShape = {
    display_name: optionalMaxString(SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH),
    title: optionalMaxString(SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH),
    signature_name: optionalMaxString(SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH),
};

const sizeSchema = Yup.mixed<'small' | 'medium' | 'large'>()
    .oneOf(['small', 'medium', 'large'])
    .required();

/** Defaults for the unified create-signature form (common fields + method defaults). */
export const createSignatureDefaultValues: CreateSignatureFormInterface = {
    creation_method: 'typed',
    typed_text: '',
    font_family: SIGNATURE_DEFAULTS.font,
    color: SIGNATURE_DEFAULTS.color,
    size: SIGNATURE_DEFAULTS.size,
    display_name: '',
    title: '',
    signature_name: '',
    is_default: false,
};

/**
 * Method-specific defaults used when switching create method
 * (keeps common metadata fields intact).
 */
export const getMethodFieldDefaults = (
    method: SignatureCreationMethod
): Pick<
    CreateSignatureFormInterface,
    'creation_method' | 'typed_text' | 'font_family' | 'color' | 'size'
> => ({
    creation_method: method,
    typed_text: '',
    font_family: SIGNATURE_DEFAULTS.font,
    color: SIGNATURE_DEFAULTS.color,
    size: SIGNATURE_DEFAULTS.size,
});

export const createSignatureValidationSchema = Yup.object({
    creation_method: Yup.mixed<SignatureCreationMethod>()
        .oneOf(['typed', 'drawn', 'uploaded'])
        .required(),
    typed_text: Yup.string().when('creation_method', {
        is: 'typed',
        then: (schema) =>
            schema
                .trim()
                .required('Please enter your name or signature text')
                .max(
                    SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH,
                    `Text must be less than ${SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH} characters`
                ),
        otherwise: (schema) => schema.trim().nullable(),
    }),
    font_family: Yup.string().when('creation_method', {
        is: 'typed',
        then: (schema) => schema.required('Please select a font'),
        otherwise: (schema) => schema.nullable(),
    }),
    color: Yup.string().when('creation_method', {
        is: (method: SignatureCreationMethod) => method === 'typed' || method === 'drawn',
        then: (schema) => schema.required('Please select a color'),
        otherwise: (schema) => schema.nullable(),
    }),
    size: sizeSchema,
    ...metadataShape,
    is_default: Yup.boolean(),
});

export const typedSignatureDefaultValues: TypedSignatureFormInterface = {
    typed_text: '',
    font_family: SIGNATURE_DEFAULTS.font,
    color: SIGNATURE_DEFAULTS.color,
    size: SIGNATURE_DEFAULTS.size,
    display_name: '',
    title: '',
    signature_name: '',
    is_default: false,
};

export const typedSignatureValidationSchema = Yup.object({
    typed_text: Yup.string()
        .trim()
        .required('Please enter your name or signature text')
        .max(
            SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH,
            `Text must be less than ${SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH} characters`
        ),
    font_family: Yup.string().required(),
    color: Yup.string().required(),
    size: sizeSchema,
    ...metadataShape,
    is_default: Yup.boolean(),
});

export const drawnSignatureDefaultValues: DrawnSignatureFormInterface = {
    color: SIGNATURE_DEFAULTS.color,
    size: SIGNATURE_DEFAULTS.size,
    display_name: '',
    title: '',
    signature_name: '',
    is_default: false,
};

export const drawnSignatureValidationSchema = Yup.object({
    color: Yup.string().required(),
    size: sizeSchema,
    ...metadataShape,
    is_default: Yup.boolean(),
});

export const uploadSignatureDefaultValues: UploadSignatureFormInterface = {
    size: SIGNATURE_DEFAULTS.size,
    display_name: '',
    title: '',
    signature_name: '',
    is_default: false,
};

export const uploadSignatureValidationSchema = Yup.object({
    size: sizeSchema,
    ...metadataShape,
    is_default: Yup.boolean(),
});

export const editSignatureValidationSchema = Yup.object({
    ...metadataShape,
});
